import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { DisputeEvent } from "@/data/contractor/networkExecution/dispute/dispute";

// ── DB row shape — matches dispute_events table ───────────────────────
interface DisputeEventRow {
  id: string;
  dispute_id: string;
  type: string;
  callback_id: string | null;
  holdback_id: string | null;
  job_id: string;
  contractor_id: string;
  actor_id: string;
  actor_role: string;
  reason: string;
  created_at: string;
  outcome: string | null;
  enforcement_mode_at_write: string;
  organization_id: string;
}

// ── Mappers ───────────────────────────────────────────────────────────
function toUiEvent(row: DisputeEventRow): DisputeEvent {
  return {
    id: row.id,
    disputeId: row.dispute_id,
    type: row.type as DisputeEvent["type"],
    callbackId: row.callback_id ?? undefined,
    holdbackId: row.holdback_id ?? undefined,
    jobId: row.job_id,
    contractorId: row.contractor_id,
    actorId: row.actor_id,
    actorRole: row.actor_role,
    reason: row.reason,
    createdAt: row.created_at,
    outcome: (row.outcome as DisputeEvent["outcome"]) ?? undefined,
    enforcementModeAtWrite: row.enforcement_mode_at_write as DisputeEvent["enforcementModeAtWrite"],
  };
}

function toDbRow(
  event: DisputeEvent,
  organizationId: string | null,
): Omit<DisputeEventRow, "created_at"> & { created_at?: string } {
  return {
    id: event.id,
    dispute_id: event.disputeId,
    type: event.type,
    callback_id: event.callbackId ?? null,
    holdback_id: event.holdbackId ?? null,
    job_id: event.jobId,
    contractor_id: event.contractorId,
    actor_id: event.actorId,
    actor_role: event.actorRole,
    reason: event.reason,
    created_at: event.createdAt,
    outcome: event.outcome ?? null,
    enforcement_mode_at_write: event.enforcementModeAtWrite,
    organization_id: organizationId as string,
  };
}

// ── Service ───────────────────────────────────────────────────────────
export const DisputeEventService = {
  async getAll(): Promise<{ data: DisputeEvent[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("dispute_events")
      .select("*")
      .order("created_at", { ascending: true });

    if (error || !data) return { data: null, error };
    return { data: (data as DisputeEventRow[]).map(toUiEvent), error: null };
  },

  async getByDisputeId(disputeId: string): Promise<{ data: DisputeEvent[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("dispute_events")
      .select("*")
      .eq("dispute_id", disputeId)
      .order("created_at", { ascending: true });

    if (error || !data) return { data: null, error };
    return { data: (data as DisputeEventRow[]).map(toUiEvent), error: null };
  },

  async append(event: DisputeEvent): Promise<{ data: DisputeEvent | null; error: unknown }> {
    let organizationId: string | null = null;
    try { organizationId = await getCurrentOrganizationId(); } catch { /* proceed without */ }

    const row = toDbRow(event, organizationId);

    const { data, error } = await supabase
      .from("dispute_events")
      .insert([row])
      .select()
      .single();

    if (error || !data) {
      console.error("[DisputeEventService.append] error:", JSON.stringify(error));
      return { data: null, error };
    }
    return { data: toUiEvent(data as DisputeEventRow), error: null };
  },
};
